"use client";
import { useEscSuppressibleTooltip } from "@/hooks/use-esc-suppressible-tooltip";
import { LocaleData } from "@/types/i18n";
import { generateQualityLink } from "@/utils/links";

const SYSTEM_LINKS: Record<string, string> = {
  norwegian:
    "https://kanalregister.hkdir.no/en/informasjonsartikler/levels-and-changes-in-levels",
  jufo: "https://julkaisufoorumi.fi/en/evaluations/classification-criteria",
};

export function qualitySystemName(system: string): string {
  return system === "jufo" ? "JUFO" : system.charAt(0).toUpperCase() + system.slice(1);
}

interface QualityScoreEntryProps {
  system: string;
  score: string | null | undefined;
  localizedData: LocaleData | undefined;
  linkTarget: string;
  orgSlug: string;
  inline?: boolean;
}

export function QualityScoreEntry({
  system,
  score,
  localizedData,
  linkTarget,
  orgSlug,
  inline = false,
}: QualityScoreEntryProps) {
  const tooltip = useEscSuppressibleTooltip();
  const scoreValue = score ?? null;
  const label =
    scoreValue != null
      ? (localizedData?.[
          `quality_labels_level${scoreValue}` as keyof LocaleData
        ] as string) || `Level ${scoreValue}`
      : localizedData?.quality_labels_unscored || "Unscored";
  const tooltipText =
    scoreValue != null
      ? (localizedData?.[
          `quality_tooltips_${system}_level${scoreValue}` as keyof LocaleData
        ] as string)
      : (localizedData?.[
          `quality_tooltips_${system}_unscored` as keyof LocaleData
        ] as string);

  const systemName = qualitySystemName(system);
  const baseClass = `tooltip cursor-pointer${inline ? " inline-block" : ""}`;

  if (scoreValue != null) {
    return (
      <div
        className={`${baseClass} ${tooltip.isSuppressed ? "tooltip-escape-hidden" : ""}`}
        onMouseLeave={tooltip.resetSuppressed}
      >
        {SYSTEM_LINKS[system] ? (
          <a
            ref={tooltip.setTriggerRef}
            href={generateQualityLink(system, orgSlug)}
            target={linkTarget}
            rel={linkTarget === "_blank" ? "noopener noreferrer" : undefined}
            onKeyDown={tooltip.onKeyDown}
            onBlur={tooltip.resetSuppressed}
          >
            {systemName}: {label}
            {tooltipText && <span className="sr-only">{tooltipText}</span>}
          </a>
        ) : (
          <span
            ref={tooltip.setTriggerRef}
            onKeyDown={tooltip.onKeyDown}
            onBlur={tooltip.resetSuppressed}
          >
            {systemName}: {label}
            {tooltipText && <span className="sr-only">{tooltipText}</span>}
          </span>
        )}
        <span className="tooltiptext" aria-hidden="true">
          {tooltipText}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={tooltip.setTriggerRef}
      className={`${baseClass} ${tooltip.isSuppressed ? "tooltip-escape-hidden" : ""}`}
      tabIndex={0}
      onKeyDown={tooltip.onKeyDown}
      onBlur={tooltip.resetSuppressed}
      onMouseLeave={tooltip.resetSuppressed}
    >
      {systemName}: {label}
      {tooltipText && <span className="sr-only">{tooltipText}</span>}
      <span className="tooltiptext" aria-hidden="true">
        {tooltipText}
      </span>
    </div>
  );
}
